/**
 * Готовые окна отправки: прайс-лист и карта цветов.
 *
 * Оба берут получателей из справочника — врачей с почтой и юрлица клиник —
 * и отдают их общему окну `SendDocDialog`. Разница только во вложении и в
 * том, кому вообще можно писать.
 */

import { useEffect, useState } from 'react';
import type { PriceListModel } from '@otc/shared';
import { api } from '../api/client.js';
import { useBootstrap } from '../api/queries.js';
import { SendDocDialog, type SendTarget } from './SendDocDialog.js';

/** Врачи с почтой: кому можно написать. Подпись — их клиники. */
function useПолучатели() {
  const bootstrap = useBootstrap(true);
  const clinics = bootstrap.data?.clinics ?? [];

  const имяКлиники = (id: string) => clinics.find((к) => к.id === id)?.name ?? '';

  const doctors: SendTarget[] = (bootstrap.data?.doctors ?? [])
    .filter((в) => !в.archived)
    .map((в) => ({
      id: в.id,
      name: в.name,
      email: в.email,
      note: в.clinics.map(имяКлиники).filter(Boolean).join(', '),
    }));

  const legals: SendTarget[] = clinics.flatMap((к) =>
    к.legals.map((ю) => ({
      id: ю.id,
      name: `${ю.shortForm} «${ю.name}»`,
      email: ю.email ?? '',
      note: к.name,
    })),
  );

  return { doctors, legals, загружается: bootstrap.isLoading };
}

/** Прайс-лист письмом: врачам и клиникам, вложением — то, что на экране. */
export function SendPriceListDialog({
  model,
  onClose,
  onDone,
}: {
  model: PriceListModel;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { doctors, legals } = useПолучатели();

  return (
    <SendDocDialog
      title="Отправить прайс-лист"
      doctors={doctors}
      legals={legals}
      subject="Прайс-лист зуботехнической лаборатории OrthoTech"
      body={
        'Здравствуйте!\n\n' +
        'Отправляем вам прайс-лист на ортодонтические конструкции.\n' +
        'Он во вложении, в формате PDF.\n\n' +
        'С уважением,\nзуботехническая лаборатория OrthoTech'
      }
      attachment={`Прайс-лист OrthoTech ${model.date}.pdf`}
      onClose={onClose}
      onDone={onDone}
      onSend={(кому) => api.post('/api/price-list/send', { ...кому, model })}
    />
  );
}

/**
 * Наряд письмом: врачам и клиникам.
 *
 * Вложение собирается на сервере из ТОГО ЖЕ наряда, что на экране, — с его
 * колонками, подытогами и правками. Собирать PDF в браузере нельзя: его
 * рисует тот же код, что и при выгрузке, и делает это сервер.
 */
export function SendReportDialog({
  report,
  onClose,
  onDone,
}: {
  /** Наряд ровно в том виде, в каком его выгружают кнопкой «Скачать PDF». */
  report: unknown;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { doctors, legals } = useПолучатели();
  const лист = report as { clinicName?: string; date?: string };

  return (
    <SendDocDialog
      title="Отправить наряд"
      doctors={doctors}
      legals={legals}
      subject={`Наряд зуботехнической лаборатории OrthoTech${
        лист.clinicName ? ` · ${лист.clinicName}` : ''
      }`}
      body={
        'Здравствуйте!\n\n' +
        'Отправляем вам наряд по выполненным работам.\n' +
        'Он во вложении, в формате PDF.\n\n' +
        'С уважением,\nзуботехническая лаборатория OrthoTech'
      }
      attachment={`Наряд${лист.clinicName ? ` · ${лист.clinicName}` : ''}.pdf`}
      onClose={onClose}
      onDone={onDone}
      onSend={(кому) => api.post('/api/reports/send', { ...кому, report })}
    />
  );
}

/**
 * Карта цветов письмом — ТОЛЬКО врачам.
 *
 * Вложение — готовый файл поставщика: перерисовывать карту нам нечем, её
 * печатает завод. Файл лежит на сервере и заменяется прямо в этом окне:
 * пришла новая карта — загрузили, и письма пойдут с ней.
 */
export function SendColorMapDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const { doctors } = useПолучатели();
  const [файл, setФайл] = useState<string | null | undefined>(undefined);

  const перечитать = () =>
    api
      .get<{ file: string | null }>('/api/color-map')
      .then((о) => setФайл(о.file))
      .catch(() => setФайл(null));

  useEffect(() => {
    void перечитать();
  }, []);

  return (
    <SendDocDialog
      title="Отправить карту цветов"
      doctors={doctors}
      subject="Карта цветов зуботехнической лаборатории OrthoTech"
      body={
        'Здравствуйте!\n\n' +
        'Отправляем вам карту цветов для ортодонтических аппаратов.\n' +
        'Она во вложении, в формате PDF.\n\n' +
        'С уважением,\nзуботехническая лаборатория OrthoTech'
      }
      attachment={
        файл === undefined
          ? 'Проверяем файл…'
          : (файл ?? 'Файл карты цветов не загружен — нажмите «Заменить файл»')
      }
      onAttachmentReplace={async (file) => {
        /* Файл уходит обычной формой: общий `api` умеет только JSON. */
        const форма = new FormData();
        форма.append('file', file);
        const ответ = await fetch('/api/color-map', {
          method: 'POST',
          body: форма,
          credentials: 'same-origin',
        });
        if (!ответ.ok) {
          const тело = (await ответ.json().catch(() => ({}))) as { error?: string };
          throw new Error(тело.error ?? 'Не удалось загрузить файл');
        }
        await перечитать();
      }}
      onClose={onClose}
      onDone={onDone}
      onSend={(кому) => api.post('/api/color-map/send', кому)}
    />
  );
}
